import axios, {
  CancelTokenSource,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import { ACCOUNT_EXIST, ACCOUNT_SUSPENDED, TOKEN_EXPIRED } from "../api/code";
import { authLogout, authRefresh } from "../api/auth";

export const LOCAL_STORAGE_ACCESS_KEY = "runner_access_token";
export const LOCAL_STORAGE_SUPER_ADMIN_KEY = "runner_super_admin_token";
export const LOCAL_STORAGE_TOKEN_TYPE = "Bearer";
export const LOCAL_STORAGE_PUB_ID = "runner_pub_id";
export const LOCAL_STORAGE_USER_ID = "runner_user_id";

/** 로그인 완료: 셀러 accessToken + 선택된 pubId 모두 존재 */
export function hasSellerSession(): boolean {
  return (
    !!localStorage.getItem(LOCAL_STORAGE_PUB_ID) &&
    !!localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY)
  );
}

const network = axios.create({
  baseURL: process.env.REACT_APP_WEB_API_HOST + "",
});

// 로딩 상태 관리를 위한 변수들
let loadingCount = 0;
let setLoadingState: ((loading: boolean) => void) | null = null;

// 로딩 상태 설정 함수 등록 (앱 초기화 시 호출) 리코일 못 써서 그래요
export function setLoadingStateSetter(setter: (loading: boolean) => void) {
  setLoadingState = setter;
}

// 로딩 상태 업데이트 함수
function updateLoadingState() {
  if (setLoadingState) {
    setLoadingState(loadingCount > 0);
  }
}

// 로딩 시작
function startLoading() {
  loadingCount++;
  updateLoadingState();
}

// 로딩 종료
function endLoading() {
  loadingCount = Math.max(0, loadingCount - 1);
  updateLoadingState();
}

// 체크용
interface PendingRequest {
  cancelTokenSource: CancelTokenSource;
  timer: NodeJS.Timeout;
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface FailedRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  config: RetryableRequestConfig;
}

let isRefreshing = false;
let failedRequestQueue: FailedRequest[] = [];
let sessionExpiredHandled = false;

async function handleSessionExpired() {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  alert("로그인이 만료되었습니다.");
  await forceLogout();
}

function processFailedRequestQueue(
  error: unknown | null,
  token: string | null,
) {
  failedRequestQueue.forEach(({ resolve, reject, config }) => {
    if (error || !token) {
      reject(error);
      return;
    }
    config.headers.Authorization = token;
    resolve(network.request(config));
  });
  failedRequestQueue = [];
}

const pendingRequests: { [key: string]: PendingRequest } = {};
const DEBOUNCE_DELAY = 100;

// 요청의 고유 키를 생성하는 함수
function getRequestKey(config: AxiosRequestConfig): string {
  return `${config.method}:${config.url}`;
}

function getStoredPubId(): number | null {
  const value = localStorage.getItem(LOCAL_STORAGE_PUB_ID);
  return value ? Number(value) : null;
}

function getStoredUserId(): number | null {
  const value = localStorage.getItem(LOCAL_STORAGE_USER_ID);
  return value ? Number(value) : null;
}

network.interceptors.request.use(async (config) => {
  if (!navigator.onLine) {
    alert("인터넷 연결이 불안정합니다. 네트워크 상태를 확인해주세요.");
    return config;
  }

  const requestKey = getRequestKey(config);

  // 이전 같은 요청이 있다면 취소
  if (pendingRequests[requestKey]) {
    pendingRequests[requestKey].cancelTokenSource.cancel(
      "Duplicate request canceled",
    );
    clearTimeout(pendingRequests[requestKey].timer);
    endLoading(); // 취소된 요청의 로딩 상태 해제
  }

  // 새로운 CancelToken 생성
  const cancelTokenSource = axios.CancelToken.source();
  config.cancelToken = cancelTokenSource.token;

  // 로딩 시작
  const excludeLoadingPaths = [
    "/seller/tournament/detail",
    "/seller/controller-log",
    "/seller/tournament/participation/is-sitout",
    "/seller/runner-rankings/itm-ranking",
    "/seller/runner-rankings/ranking",
  ];
  if (!excludeLoadingPaths.some((path) => requestKey.includes(path))) {
    startLoading();
  }

  // 취소 로직
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      const headers = config.headers;
      const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
      if (accessToken !== null) {
        headers["Authorization"] = accessToken;
      }
      delete pendingRequests[requestKey]; // 요청이 시작되면 pending 목록에서 제거
      resolve(config);
    }, DEBOUNCE_DELAY);

    pendingRequests[requestKey] = { cancelTokenSource, timer };
  });
});

network.interceptors.response.use(
  async (res) => {
    const requestKey = getRequestKey(res.config);
    delete pendingRequests[requestKey]; // 응답이 오면 pending 목록에서 제거

    // 로딩 종료
    endLoading();

    if (res.data) {
      if (res.data.code && res.data.code !== 200) {
        throw res.data;
      }

      return res.data;
    }
  },
  async (e: any) => {
    // 로딩 종료
    endLoading();

    if (axios.isCancel(e)) {
      console.log("Request canceled:", e.message);
      return;
    }

    const requestKey = e.config ? getRequestKey(e.config) : null;
    if (requestKey) {
      delete pendingRequests[requestKey]; // 에러 발생 시에도 pending 목록에서 제거
    }

    let errorMsg = e.message;

    if (e.response) {
      const config = e.config as RetryableRequestConfig;
      const pubId = getStoredPubId();
      const userId = getStoredUserId();

      if (
        e.response.status === 401 &&
        config &&
        !config._retry &&
        pubId &&
        userId
      ) {
        // 무한 루프 방지: refresh 요청 자체는 재시도하지 않음
        if (config.url && config.url.includes("/seller/auth/refresh")) {
          await handleSessionExpired();
          return Promise.reject(e);
        }

        config._retry = true;

        // 토큰 갱신 요청 중일 경우 대기 큐에 추가
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedRequestQueue.push({ resolve, reject, config });
          });
        }

        isRefreshing = true;

        try {
          // refreshToken으로 새로운 accessToken 받기 (쿠키에 http-only로 저장된 refreshToken 자동 전송)
          const refreshResponse = await authRefresh(pubId, userId);

          if (
            refreshResponse &&
            refreshResponse.payload &&
            refreshResponse.payload.accessToken
          ) {
            // 새 토큰 저장 (로그인과 동일하게 Bearer 포함)
            const newToken = `${LOCAL_STORAGE_TOKEN_TYPE} ${refreshResponse.payload.accessToken}`;
            localStorage.setItem(LOCAL_STORAGE_ACCESS_KEY, newToken);

            // 대기 큐에 있는 요청 재시도
            processFailedRequestQueue(null, newToken);

            // 원래 요청 재시도
            config.headers.Authorization = newToken;
            return network.request(config);
          }

          // 대기 큐에 있는 요청 재시도
          processFailedRequestQueue(e, null);
          await handleSessionExpired();
          return Promise.reject(e);
        } catch (refreshError) {
          // 대기 큐에 있는 요청 재시도
          processFailedRequestQueue(refreshError, null);
          console.error("Token refresh failed:", refreshError);
          if (isNetworkError(refreshError)) {
            return Promise.reject(refreshError);
          }
          if (isAuthError(refreshError)) {
            await handleSessionExpired();
          } else {
            await forceLogout();
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (e.response.status === 400) {
        if (e.response.data) {
          if (e.response.data.message) {
            errorMsg = `(${e.response.data.code}) ${e.response.data.message}`;
          }

          switch (e.response.data.code) {
            case TOKEN_EXPIRED:
              try {
                // 토큰 갱신 요청도 로딩 처리
                startLoading();
                localStorage.removeItem(LOCAL_STORAGE_ACCESS_KEY);
                localStorage.removeItem(LOCAL_STORAGE_SUPER_ADMIN_KEY);
                const response = await axios.post(
                  `${process.env.REACT_APP_WEB_API_HOST}/auth/refresh-token`,
                  null,
                  {
                    withCredentials: true,
                  },
                );
                endLoading();

                if (response && response.status === 200 && response.data) {
                  localStorage.setItem(
                    LOCAL_STORAGE_ACCESS_KEY,
                    `${response.data.tokenType} ${response.data.accessToken}`,
                  );
                  window.location.href = "/";
                }
              } catch (e) {
                endLoading();
              }

              break;
            case ACCOUNT_SUSPENDED:
            case ACCOUNT_EXIST:
              localStorage.removeItem(LOCAL_STORAGE_ACCESS_KEY);
              localStorage.removeItem(LOCAL_STORAGE_SUPER_ADMIN_KEY);
              break;
          }
        }
      }
    } else {
      throw e;
    }

    throw {
      ...e,
      statusCode: e.response.status,
      code: e.response.data?.code,
      message: errorMsg,
      payload: e.response.data?.payload,
    };
  },
);

export function hasCredential() {
  return localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY) !== null;
}

export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { response?: unknown; code?: string };
  return (
    !err.response || err.code === "ERR_NETWORK" || err.code === "ECONNABORTED"
  );
}

export function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { response?: { status?: number }; statusCode?: number };
  const status = err.response?.status ?? err.statusCode;
  return status === 401 || status === 403;
}

export function clearCredential() {
  localStorage.removeItem(LOCAL_STORAGE_ACCESS_KEY);
  localStorage.removeItem(LOCAL_STORAGE_SUPER_ADMIN_KEY);
  localStorage.removeItem(LOCAL_STORAGE_PUB_ID);
  localStorage.removeItem(LOCAL_STORAGE_USER_ID);
}

export async function forceLogout() {
  const pubId = getStoredPubId();
  clearCredential();
  window.location.href = "/login";
  if (pubId) {
    try {
      await authLogout(pubId);
    } catch {}
  }
}

export default network;
